import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, AlertTriangle, CheckCircle2, Search, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { COMPANY } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "title", label: "Title & Keyword Optimization", max: 20 },
  { key: "images", label: "Images & Visual Content", max: 15 },
  { key: "product", label: "Product Information", max: 15 },
  { key: "benefits", label: "Benefits & Features", max: 10 },
  { key: "aplus", label: "A+ / Enhanced Content", max: 10 },
  { key: "search", label: "Search Relevance", max: 10 },
  { key: "conversion", label: "Conversion Elements", max: 10 },
  { key: "trust", label: "Trust & Social Proof", max: 5 },
  { key: "mobile", label: "Mobile Readability", max: 5 }
];

function scoreFromInputs(fields) {
  // Deterministic transparent scoring. Every category returns 0..max.
  const s = {};
  const titleLen = (fields.title || "").length;
  s.title = clamp(Math.round((titleLen >= 80 && titleLen <= 200) ? 20 : titleLen > 40 ? 14 : titleLen > 0 ? 8 : 0), 0, 20);
  s.images = clamp(Math.round(Math.min(15, (fields.images || 0) * 2)), 0, 15);
  const bulletCount = (fields.bullets || "").split("\n").filter(x => x.trim()).length;
  s.benefits = clamp(bulletCount >= 5 ? 10 : bulletCount * 2, 0, 10);
  s.product = clamp(fields.category && fields.brand ? 15 : fields.category || fields.brand ? 9 : 3, 0, 15);
  s.aplus = fields.aplus ? 10 : 0;
  const kwCount = (fields.keywords || "").split(",").filter(x => x.trim()).length;
  s.search = clamp(Math.min(10, kwCount * 2), 0, 10);
  s.conversion = clamp((fields.hasCoupon ? 5 : 0) + (fields.mrpDiff >= 10 ? 5 : fields.mrpDiff > 0 ? 2 : 0), 0, 10);
  s.trust = clamp(fields.rating >= 4 ? 5 : fields.rating >= 3.5 ? 3 : fields.rating > 0 ? 1 : 0, 0, 5);
  s.mobile = clamp(fields.mobileTested ? 5 : 2, 0, 5);
  const total = Object.values(s).reduce((a, b) => a + b, 0);
  return { categories: s, total };
}
function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

function suggestions(scoreObj, fields) {
  const out = [];
  if (scoreObj.categories.title < 20) out.push("Increase your title to 100-180 characters with primary keyword, brand and key attribute.");
  if (scoreObj.categories.images < 12) out.push("Add more images — target 7 minimum (main + lifestyle + infographic + comparison + scale + packaging + video).");
  if (scoreObj.categories.benefits < 8) out.push("Rewrite bullets — 5 benefit-led bullets, each starting with a CAPITALISED benefit phrase.");
  if (scoreObj.categories.aplus === 0) out.push("Add A+ content / Enhanced Brand Content — non-negotiable for hero SKUs on Amazon.");
  if (scoreObj.categories.search < 8) out.push("Add 5+ backend keywords / phrase-match keywords. Include category and long-tail terms.");
  if (scoreObj.categories.conversion < 8) out.push("Add a coupon and ensure MRP-to-Selling-Price gap is at least 10% for urgency.");
  if (scoreObj.categories.trust < 5) out.push("Push reviews aggressively for first 90 days — Vine, post-purchase email, verified-review programs.");
  return out.slice(0, 5);
}

export default function ListingScoreTool() {
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [detected, setDetected] = useState(null);
  const [fields, setFields] = useState({
    title: "",
    images: 0,
    bullets: "",
    category: "",
    brand: "",
    aplus: false,
    keywords: "",
    hasCoupon: false,
    mrpDiff: 0,
    rating: 0,
    mobileTested: false
  });
  const [score, setScore] = useState(null);

  async function tryFetch() {
    setFetching(true);
    setFetchError(null);
    try {
      const r = await fetch(`${API}/api/listing/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      const data = await r.json();
      if (data.success) {
        setDetected(data);
        // Pre-fill best-guess signals into the form
        setFields((f) => ({
          ...f,
          title: data.title || f.title,
          images: data.signals?.image_count ? Math.min(10, Math.floor(data.signals.image_count / 3)) : f.images,
          aplus: !!data.signals?.has_video || f.aplus,
          rating: data.signals?.has_rating ? 4.2 : f.rating,
        }));
        setFetchError(null);
      } else {
        setDetected({ marketplace: data.marketplace, success: false });
        setFetchError(data.message || "Automatic analysis isn't available for this URL. Enter your listing details manually below.");
      }
    } catch (e) {
      setFetchError("Automatic analysis failed. Enter your listing details manually below.");
    } finally {
      setFetching(false);
    }
  }

  function runScore() {
    setScore(scoreFromInputs(fields));
    setTimeout(() => {
      document.getElementById("score-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }

  const num = COMPANY.whatsapp.replace(/\D/g, "");
  const waMsg = encodeURIComponent("Hi Orina Enterprise, I completed the Listing Score audit and want help improving my marketplace listing.");

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Marketplace Listing Score Tool",
    "url": "https://orinaenterprise.com/tools/listing-score/",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "isAccessibleForFree": true,
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
    "provider": { "@id": "https://orinaenterprise.com/#organization" }
  };

  return (
    <>
      <SEO
        title="Free Marketplace Listing Score — Score Your Product Listing Out of 100"
        description="Paste your Amazon, Flipkart, Meesho, JioMart, Myntra, AJIO or Nykaa listing URL and get a free listing quality score with actionable fixes. Manual audit fallback available."
        keywords="marketplace listing score, Amazon listing audit, Flipkart listing checker, free listing audit India"
        path="/tools/listing-score"
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Tools", path: "/tools/marketplace-calculators" }, { name: "Listing Score", path: "/tools/listing-score" }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Free Tool</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            Free Marketplace Listing Score — Score Your Product Listing Out of 100
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">
            Paste a listing URL (Amazon, Flipkart, Meesho, JioMart, Myntra, AJIO, Nykaa, Tata CLiQ) or enter details manually. Get a transparent 100-point score with actionable fixes.
          </p>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="card-brutal">
            <div className="text-xs font-bold uppercase tracking-widest text-black/60">Step 1</div>
            <div className="mt-2 text-lg font-black">Paste your product listing URL</div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input data-testid="listing-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.amazon.in/... or Flipkart / Meesho / etc." className="flex-1 border-2 border-black px-3 py-3 text-base" />
              <button data-testid="listing-fetch-btn" disabled={!url || fetching} onClick={tryFetch} className="btn-brutal">
                {fetching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Analyse
              </button>
            </div>
            {detected?.success && (
              <div className="mt-4 border-2 border-black bg-[#FAFAF7] p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-[#25D366]">Detected marketplace: {detected.marketplace}</div>
                <div className="mt-1 text-sm font-bold">{detected.title}</div>
                {detected.excerpt && <p className="mt-2 text-xs text-black/70">{detected.excerpt}</p>}
                <p className="mt-2 text-xs text-black/50">Auto-detected signals were pre-filled below. Please verify and enrich manually.</p>
              </div>
            )}
            {fetchError && (
              <div className="mt-4 flex gap-2 border-2 border-black bg-[#FFD700] p-4 text-sm">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{fetchError}</span>
              </div>
            )}
            <p className="mt-4 text-xs text-black/60">
              We honour marketplace robots.txt and never bypass CAPTCHA, login walls or anti-bot controls. If automatic analysis is not permitted, use the manual form below.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t-2 border-black bg-[#FAFAF7] py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="text-xs font-bold uppercase tracking-widest text-black/60">Step 2</div>
          <h2 className="display mt-2 text-2xl font-black md:text-3xl">Enter your listing details</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase tracking-widest">Product title</span><input value={fields.title} onChange={(e) => setFields({ ...fields, title: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block"><span className="text-xs font-bold uppercase tracking-widest">Brand</span><input value={fields.brand} onChange={(e) => setFields({ ...fields, brand: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block"><span className="text-xs font-bold uppercase tracking-widest">Category</span><input value={fields.category} onChange={(e) => setFields({ ...fields, category: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block"><span className="text-xs font-bold uppercase tracking-widest">Image count</span><input type="number" value={fields.images} onChange={(e) => setFields({ ...fields, images: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block"><span className="text-xs font-bold uppercase tracking-widest">MRP-to-SP gap (%)</span><input type="number" value={fields.mrpDiff} onChange={(e) => setFields({ ...fields, mrpDiff: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase tracking-widest">Bullets (one per line)</span><textarea rows={5} value={fields.bullets} onChange={(e) => setFields({ ...fields, bullets: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase tracking-widest">Backend keywords (comma-separated)</span><input value={fields.keywords} onChange={(e) => setFields({ ...fields, keywords: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="block"><span className="text-xs font-bold uppercase tracking-widest">Current rating (0-5)</span><input type="number" step="0.1" value={fields.rating} onChange={(e) => setFields({ ...fields, rating: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></label>
            <label className="flex items-center gap-2 pt-4"><input type="checkbox" checked={fields.aplus} onChange={(e) => setFields({ ...fields, aplus: e.target.checked })} className="h-4 w-4" /><span className="text-sm font-bold">A+ content is live</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={fields.hasCoupon} onChange={(e) => setFields({ ...fields, hasCoupon: e.target.checked })} className="h-4 w-4" /><span className="text-sm font-bold">Coupon / bank offer is active</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={fields.mobileTested} onChange={(e) => setFields({ ...fields, mobileTested: e.target.checked })} className="h-4 w-4" /><span className="text-sm font-bold">I've QA'd on mobile</span></label>
          </div>
          <button data-testid="listing-score-btn" onClick={runScore} className="btn-brutal mt-8"><Sparkles size={16} /> Get my listing score</button>
        </div>
      </section>

      {score && (
        <section id="score-result" className="border-t-2 border-black bg-white py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <div className="grid gap-8 md:grid-cols-2">
              <div className="border-2 border-black bg-[#FFD700] p-6 text-center shadow-brutal-lg">
                <div className="text-xs font-bold uppercase tracking-widest">Overall listing score</div>
                <div className="display mt-2 text-7xl font-black" data-testid="listing-score">{score.total}<span className="text-3xl text-black/50"> / 100</span></div>
                <ScoreRing pct={score.total} />
                <div className="mt-4 text-sm font-bold uppercase tracking-widest">
                  {score.total >= 80 ? "Excellent" : score.total >= 60 ? "Good — with room to grow" : score.total >= 40 ? "Needs work" : "Critical fixes needed"}
                </div>
              </div>
              <div className="border-2 border-black bg-black p-6 text-white">
                <div className="text-xs font-bold uppercase tracking-widest text-[#FFD700]">Category breakdown</div>
                <div className="mt-3 space-y-2">
                  {CATEGORIES.map((c) => (
                    <div key={c.key}>
                      <div className="flex justify-between text-xs"><span>{c.label}</span><span className="font-bold text-[#FFD700]">{score.categories[c.key]}/{c.max}</span></div>
                      <div className="mt-1 h-2 border border-white/30">
                        <div className="h-full bg-[#FFD700]" style={{ width: `${(score.categories[c.key] / c.max) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 border-2 border-black bg-[#FAFAF7] p-6">
              <h3 className="display text-xl font-black">Fix these 3 things first</h3>
              <ul className="mt-4 space-y-2">
                {suggestions(score, fields).map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm md:text-base">
                    <CheckCircle2 className="mt-1 shrink-0 text-[#25D366]" size={18} /> {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact/?type=expert" className="btn-brutal">Get my listing fixed <ArrowUpRight size={16} /></Link>
              <a href={`https://wa.me/${num}?text=${waMsg}`} target="_blank" rel="noreferrer" className="btn-brutal secondary">Talk to Orina on WhatsApp</a>
            </div>
          </div>
        </section>
      )}

      <CtaBanner title="Want Orina Enterprise to fix this listing?" subtitle="We rewrite listings, redesign A+ content, and set up conversion-focused pricing in 7-14 days." />
    </>
  );
}

function ScoreRing({ pct }) {
  const size = 120, stroke = 12, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto mt-4 h-28 w-28">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(0,0,0,0.15)" strokeWidth={stroke} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#000" strokeWidth={stroke} fill="none" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset 900ms ease-out" }} />
      <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="900" fill="#000">{pct}</text>
    </svg>
  );
}
